import { Component, ElementRef, EventEmitter, Input, OnInit, OnChanges, AfterViewInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';

const colorPalette = [
  '#000000', '#ffffff', '#E53935', '#D81B60', '#E91E63', '#8E24AA', '#9C27B0', '#AB47BC', '#5E35B1', '#3949AB',
  '#5C6BC0', '#1E88E5', '#2196F3', '#42A5F5', '#039BE5', '#00ACC1', '#26C6DA', '#26A69A', '#4DB6AC', '#43A047',
  '#4CAF50', '#66BB6A', '#7CB342', '#9CCC65', '#C0CA33', '#D4E157', '#FDD835', '#FFEB3B', '#FFC107', '#FFB300',
  '#FF9800', '#FB8C00', '#F4511E', '#8D6E63', '#795548', '#6D4C41', '#757575', '#9E9E9E', '#BDBDBD', '#546E7A',
  '#607D8B'
];
@Component({
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss']
})
export class EditorComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() innerValue: string;
  @Output() innerValueChange = new EventEmitter<string>();
  @Input() hideToolbar: boolean = false;

  @ViewChild('textArea', { static: false }) textAreaRef: ElementRef<HTMLElement>;

  wordCount: number = 0;
  showTextColorOptions: boolean = false;
  showBgColorOptions: boolean = false;
  selectedTextColor: string = '#000000';
  selectedBgColor: string = '#ffffff';
  colorPalette: string[] = colorPalette;
  formatBlock: string = 'P'
  floatingToolbar: boolean = true;

  constructor(private renderer: Renderer2,) { }

  private getTextArea(): HTMLElement | null {
    return this.textAreaRef?.nativeElement || null;
  }

  ngOnInit(): void {
    // Initialize any logic needed on component initialization
  }

  ngAfterViewInit(): void {
    // Set initial value after view is initialized
    setTimeout(() => {
      this.updateEditorContent();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update editor content when innerValue changes
    if (changes['innerValue']) {
      if (changes['innerValue'].firstChange) {
        // Handle initial value - wait for view to be initialized
        setTimeout(() => this.updateEditorContent(), 0);
      } else {
        // Only update if the value actually changed and view is initialized
        if (changes['innerValue'].previousValue !== changes['innerValue'].currentValue) {
          // Use setTimeout to ensure ViewChild is available
          setTimeout(() => this.updateEditorContent(), 0);
        }
      }
    }
  }

  private updateEditorContent(): void {
    const textArea = this.getTextArea();
    if (textArea) {
      if (this.innerValue === undefined || this.innerValue === null) {
        this.innerValue = '';
      }
      // Only update if the content has actually changed to avoid cursor position issues
      if (textArea.innerHTML !== this.innerValue) {
        textArea.innerHTML = this.innerValue;
      }
    }
  }
  executeCommand(command: string, value?: any) {
    if (command === 'createLink' && value) {
      const selectedText = window.getSelection()?.toString();
      if (selectedText) {
        const link = prompt('Enter the URL for the link:', 'http://');
        if (link) {
          const anchor = document.createElement('a');
          anchor.href = link;
          anchor.textContent = selectedText;
          anchor.target = '_blank'; // Optional: open link in a new tab
          const range = window.getSelection()?.getRangeAt(0);
          range.deleteContents();
          range.insertNode(anchor);
        }
      }
    } else {
      document.execCommand(command, false, value);
    }
    setTimeout(() => this.focusTextArea(), 0);
    if (command === 'foreColor') {
      this.selectedTextColor = value; // Update selected text color
    }
    if (command === 'backColor') {
      this.selectedBgColor = value; // Update selected bg color
    }
  }

  updateWordCount(event: any) {
    const text = event.target.innerText.trim();
    const words = text.split(/\s+/).filter(word => word.length > 0);
    this.wordCount = words.length;
    this.autoResizeTextArea(event)
    this.innerValueChange.emit(event.target.innerHTML);
  }
  exportContent() {
    const textArea = this.getTextArea();
    if (!textArea) return;
    const content = textArea.innerHTML;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'content.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  toggleColorPicker(type: string) {
    if (type === 'text-color') {
      this.showTextColorOptions = !this.showTextColorOptions;
    } else if (type === 'bg-color') {
      this.showBgColorOptions = !this.showBgColorOptions;
    }
  }


  handleImageUpload(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const imgSrc = e.target.result;
        this.insertImage(imgSrc);
        this.addSpaceToTextArea(); // Trigger text-area to add space
      };
      reader.readAsDataURL(file);
    }
  }

  addSpaceToTextArea() {
    const textArea = this.getTextArea();
    if (!textArea) return;
    textArea.innerHTML += '&nbsp;'; // Add a non-breaking space
    const event = new Event('keyup');
    textArea.dispatchEvent(event); // Trigger keyup event
    this.innerValueChange.emit(textArea.innerHTML);
  }

  insertImage(imgSrc: string) {
    const image = new Image();
    image.src = imgSrc;
    image.style.maxWidth = '50%';
    image.style.cursor = 'move'; // Add cursor style for dragging
    const textArea = this.getTextArea();
    if (!textArea) return;

    // Get the active cursor (caret) position in the text-area element
    let selection = window.getSelection();
    let range = null;
    if (selection && selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
      // Ensure the selection is within the text-area
      if (!textArea.contains(range.commonAncestorContainer)) {
        range = null;
      }
    }

    if (range) {
      // Insert the image at the current cursor position
      range.collapse(false); // collapse to end of selection
      range.insertNode(image);

      // Move the cursor after the inserted image
      range.setStartAfter(image);
      range.setEndAfter(image);
      selection?.removeAllRanges();
      selection?.addRange(range);
    } else {
      // If no valid range, append at the end
      textArea.appendChild(image);
    }
  }

  triggerImageUpload() {
    // Use querySelector within the component's element to find the image upload
    const textArea = this.getTextArea();
    if (textArea) {
      const editorElement = textArea.closest('.editor');
      const imageUpload = editorElement?.querySelector('#image-upload') as HTMLInputElement;
      if (imageUpload) {
        imageUpload.click();
      }
    }
  }

  updateFontSizeDropdown() {
    const selection = document.getSelection();
    const parentElement = selection?.anchorNode?.parentNode as HTMLElement;
    const fontSize = parentElement?.style?.fontSize;
    const textArea = this.getTextArea();
    if (textArea && fontSize) {
      const editorElement = textArea.closest('.editor');
      const fontSizeDropdown = editorElement?.querySelector('#font-size') as HTMLSelectElement;
      if (fontSizeDropdown) {
        fontSizeDropdown.value = fontSize;
      }
    }
  }

  autoResizeTextArea(event: Event) {
    const textArea = event.target as HTMLElement;
    textArea.style.height = 'auto';
    textArea.style.height = textArea.scrollHeight + 'px';
  }

  focusTextArea() {
    const textArea = this.getTextArea();
    if (textArea) {
      textArea.focus();
    }
  }

  addHyperlink() {
    const url = prompt('Enter the URL for the link:', 'http://');
    let text = prompt('Enter URL placeholder', 'Click Here');
    if (url && text) {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.textContent = text;
      anchor.classList.add('text-primary');
      anchor.style.textDecoration = 'underline';
      anchor.target = '_blank';
      const textArea = this.getTextArea();
      if (textArea) {
        textArea.appendChild(anchor);
        this.innerValueChange.emit(textArea.innerHTML);
      }
    }
  }

  getCurrentElementFocus() {
    const selection = document.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      let focusedElement = range.startContainer.parentElement;
      if (focusedElement && focusedElement.tagName === 'STRONG') {
        focusedElement = focusedElement.parentElement;
      }
      if (focusedElement && /^H[1-6]$/.test(focusedElement.tagName)) {
        this.formatBlock = focusedElement.tagName.toUpperCase();
        return focusedElement;
      }
    }
    this.formatBlock = 'P';
    return null;
  }
}
